import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { HardDrive, Users, GraduationCap, Plus, Minus } from "lucide-react";
import { payWithRevolut, euro } from "@/lib/revolutPay";

const ICONS = { extra_user: Users, onboarding: GraduationCap };
const SESSION_STATUS = { requested: "Demandée", scheduled: "Planifiée", done: "Réalisée", canceled: "Annulée" };

function StorageBar({ storage }) {
  const quota = storage.quota_gb * 1024 ** 3;
  const pct = Math.min(100, quota ? (storage.used_bytes / quota) * 100 : 0);
  const color = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-[#FF6A00]";
  return (
    <div data-testid="storage-usage">
      <div className="flex justify-between text-xs text-zinc-400"><span className="flex items-center gap-1.5"><HardDrive className="h-3.5 w-3.5" />Stockage documentaire</span><span>{(storage.used_bytes / 1024 ** 3).toFixed(2)} / {storage.quota_gb} Go</span></div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-zinc-800"><div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} /></div>
      {pct >= 80 && <p data-testid="storage-alert" className={`mt-1 text-xs ${pct >= 100 ? "text-red-400" : "text-amber-400"}`}>{pct >= 100 ? "Espace plein : les nouveaux fichiers sont bloqués. Ajoutez une extension ou libérez de la place." : "Vous avez utilisé plus de 80 % de votre espace."}</p>}
    </div>
  );
}

function AddonRow({ a, owned, offer, onChanged }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[a.key] || HardDrive;
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/addons", { key: a.key, quantity: qty });
      await payWithRevolut(data, {
        onSuccess: async () => { try { await api.post(`/billing/addons/${data.purchase_id}/confirm`); } catch { /* webhook will confirm */ } toast.success(a.kind === "one_off" ? "Séance réglée : nous vous contactons pour fixer la date" : "Option activée"); onChanged(); },
        onError: () => toast.error("Le paiement a échoué."),
      });
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm("Retirer une unité de cette option à partir de la prochaine facture ?")) return; await api.delete(`/billing/addons/${a.key}`); toast.success("Option retirée"); onChanged(); };
  const book = async () => { try { await api.post("/billing/onboarding/included", {}); toast.success("Séance demandée : nous vous contactons pour fixer la date"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const unavailable = a.price == null;
  return (
    <div data-testid={`addon-${a.key}`} className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-950 p-3 sm:flex-row sm:items-center">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FF6A00]/12 text-[#FF6A00]"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white">{a.name} {owned > 0 && <span data-testid={`addon-${a.key}-owned`} className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-400">{owned} actif{owned > 1 ? "s" : ""}</span>}</p>
        <p className="text-xs text-zinc-500">{unavailable ? "Disponible à partir du forfait Pro" : `${euro(a.price)} HT / ${a.unit}`} · {a.description}</p>
      </div>
      {!unavailable && (
        <div className="flex shrink-0 items-center gap-2">
          {a.key === "onboarding" && offer.onboarding_included_left > 0 && <Button data-testid="addon-onboarding-included" onClick={book} className="rounded-full bg-emerald-600 text-xs font-semibold text-white hover:bg-emerald-700">Réserver ma séance incluse</Button>}
          {a.quantity && <Input data-testid={`addon-${a.key}-qty`} type="number" min="1" max="50" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="h-8 w-16 border-zinc-800 bg-zinc-900 text-center" />}
          {owned > 0 && a.kind === "recurring" && <button data-testid={`addon-${a.key}-remove`} onClick={remove} title="Retirer" className="rounded-full p-2 text-zinc-500 hover:bg-zinc-800 hover:text-red-400"><Minus className="h-4 w-4" /></button>}
          <Button data-testid={`addon-${a.key}-buy`} onClick={buy} disabled={busy} className="rounded-full bg-[#FF6A00] text-xs font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-3.5 w-3.5" />{a.kind === "one_off" ? "Réserver" : "Ajouter"}</Button>
        </div>
      )}
    </div>
  );
}

export default function BillingAddons({ offer, onChanged }) {
  return (
    <div data-testid="addons-card" className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
      <h3 className="font-display text-lg font-bold text-white">Options</h3>
      <p className="mt-1 text-sm text-zinc-400">Le premier mois est réglé par carte à l'ajout ; les options récurrentes figurent ensuite sur votre facture mensuelle HalgarOS. {offer.users_limit} utilisateur(s) autorisé(s) actuellement.</p>
      <div className="mt-4"><StorageBar storage={offer.storage} /></div>
      <div className="mt-4 space-y-2">{offer.addons.map((a) => <AddonRow key={a.key} a={a} owned={offer.current.addons?.[a.key] || 0} offer={offer} onChanged={onChanged} />)}</div>
      {offer.onboarding_sessions.length > 0 && (
        <div className="mt-4 space-y-1" data-testid="onboarding-sessions">
          <p className="text-xs font-semibold uppercase text-zinc-500">Séances de prise en main</p>
          {offer.onboarding_sessions.map((s) => <p key={s.id} className="text-xs text-zinc-400">{new Date(s.created_at).toLocaleDateString("fr-FR")} · {s.included ? "Incluse" : "Payée"} · {SESSION_STATUS[s.status] || s.status}</p>)}
        </div>
      )}
    </div>
  );
}
